import { Grid, Card } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

import { CompanyDetailsResponse } from '@/models/payment';
import { SubTitle, Title } from '@/typography/Typography';
import { FormActionButtons } from '@/components/shared/FormActionButtons';
import { InputText } from '@/components/shared/InputText';
import { NumericInput } from '@/components/shared/NumericInput';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { GridContainer } from '@/pages/company/componants/Base';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useErrorHandling } from '@/utils/errorHandling';
import { AutoTopUpSettings } from './AutoTopUpSettings';

export const LowBalanceSettings = ({
    currentCompany
}: {
    currentCompany?: CompanyDetailsResponse;
}) => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();
    const { updateCompanyDetails } = usePaymentsMutations();
    const { backendErrorHandler } = useErrorHandling();

    const [lowBalanceEmail, setLowBalanceEmail] = useState(
        currentCompany?.lowBalanceEmail || ''
    );
    const [lowBalanceThreshold, setLowBalanceThreshold] = useState(
        currentCompany?.lowBalanceNotificationThreshold || 0
    );
    const [lowBalanceEmailError, setLowBalanceEmailError] = useState(false);
    const [lowBalanceThresholdError, setLowBalanceThresholdError] =
        useState(false);

    // Format error tracking
    const [thresholdFormatError, setThresholdFormatError] = useState(false);
    const [topUpFormatError, setTopUpFormatError] = useState(false);
    const hasFormatError = thresholdFormatError || topUpFormatError;

    // Auto Top-up state
    const isConnected = currentCompany?.autoTopupStatus === 'Connected';
    const [isAutoTopupEnabled, setIsAutoTopupEnabled] = useState(false);
    const [topUpAmount, setTopUpAmount] = useState(
        currentCompany?.autoTopupAmount || 0
    );

    // Track initial values for dirty state detection
    const [initialLowBalanceEmail, setInitialLowBalanceEmail] = useState('');
    const [initialLowBalanceThreshold, setInitialLowBalanceThreshold] =
        useState(0);
    const [initialTopUpAmount, setInitialTopUpAmount] = useState(0);

    // Compute if form is dirty
    const isDirty =
        lowBalanceEmail !== initialLowBalanceEmail ||
        lowBalanceThreshold != initialLowBalanceThreshold ||
        topUpAmount !== initialTopUpAmount;

    const initialValues = () => {
        const email = currentCompany?.lowBalanceEmail || '';
        const threshold = currentCompany?.lowBalanceNotificationThreshold || 0;
        const amount = currentCompany?.autoTopupAmount || 0;

        setLowBalanceEmail(email);
        setLowBalanceThreshold(threshold);
        setLowBalanceEmailError(false);
        setLowBalanceThresholdError(false);

        // Reset Auto Top-up state
        setIsAutoTopupEnabled(
            currentCompany?.autoTopupStatus === 'Connected' &&
                (currentCompany?.autoTopupAmount ?? 0) > 0
        );
        setTopUpAmount(amount);

        // Set initial values for dirty tracking
        setInitialLowBalanceEmail(email);
        setInitialLowBalanceThreshold(threshold);
        setInitialTopUpAmount(amount);
    };

    useEffect(() => {
        initialValues();
    }, [
        currentCompany?.lowBalanceEmail,
        currentCompany?.lowBalanceNotificationThreshold,
        currentCompany?.autoTopupStatus,
        currentCompany?.autoTopupAmount
    ]);

    useEffect(() => {
        if (updateCompanyDetails.isSuccess) {
            setAlert(t('settings_saved'), 'success');
        }
    }, [updateCompanyDetails.isSuccess]);

    useEffect(() => {
        if (updateCompanyDetails.isError) {
            backendErrorHandler()(updateCompanyDetails.error);
        }
    }, [updateCompanyDetails.isError]);

    const isValidLowBalanceData = () => {
        let isValid = true;

        // The low balance amount is always resettable to zero — the backend
        // simply excludes the company from auto top-up/notifications until it's set again.
        setLowBalanceThresholdError(false);

        if (!lowBalanceEmail) {
            setLowBalanceEmailError(true);
            isValid = false;
        } else {
            setLowBalanceEmailError(false);
        }

        return isValid;
    };

    const handleSaveChanges = () => {
        if (isValidLowBalanceData()) {
            updateCompanyDetails.mutate({
                lowBalanceEmail,
                lowBalanceNotificationThreshold: lowBalanceThreshold,
                autoTopupAmount: topUpAmount
            });
        }
    };

    const handleSaveAndConnect = async (): Promise<boolean> => {
        if (isValidLowBalanceData()) {
            try {
                await updateCompanyDetails.mutateAsync({
                    lowBalanceEmail,
                    lowBalanceNotificationThreshold: lowBalanceThreshold,
                    autoTopupAmount: topUpAmount
                });
                return true;
            } catch (_error) {
                return false;
            }
        }
        return false;
    };

    const handleCancel = () => {
        initialValues();
    };

    return (
        <Grid
            container
            mt={3}
            pt={5}
            justifyContent={'space-between'}
            borderTop={'1px solid #D0D5DD'}
        >
            <Grid size={{ xs: 3 }} display={'block'}>
                <Title>{t('low_balance')}</Title>
                <SubTitle marginBottom={2}>
                    {t('low_balance_description_1')}
                </SubTitle>
                <SubTitle marginBottom={2}>
                    {t('low_balance_description_2')}
                </SubTitle>
                {/* Add this hidden element to store the connection status */}
                <div
                    id="auto-topup-connection-status"
                    data-status={
                        currentCompany?.autoTopupStatus || 'NotConnected'
                    }
                    style={{ display: 'none' }}
                >
                    {currentCompany?.autoTopupStatus}
                </div>
            </Grid>
            <Grid size={{ xs: 8.8 }}>
                <Card>
                    <GridContainer container margin={'0 !important'}>
                        <NumericInput
                            data-testid="low-balance-threshold-input"
                            value={lowBalanceThreshold}
                            onChange={(val) => setLowBalanceThreshold(val ?? 0)}
                            onFormatError={setThresholdFormatError}
                            label={t('set_low_balance_amount')}
                            placeholder={t(
                                'company.low_balance_default_placeholder'
                            )}
                            error={lowBalanceThresholdError}
                            helperText={
                                lowBalanceThresholdError &&
                                isConnected &&
                                isAutoTopupEnabled &&
                                topUpAmount > 0
                                    ? t(
                                          'company.low_balance_field_not_empty_topup'
                                      )
                                    : ''
                            }
                        />
                        <SubTitle>{t('company.low_balance_info')}</SubTitle>
                    </GridContainer>
                    <GridContainer container>
                        <InputText
                            value={lowBalanceEmail}
                            onChange={(e) => setLowBalanceEmail(e.target.value)}
                            label={t('low_balance_notification')}
                            placeholder={t(
                                'company.low_balance_email_placeholder'
                            )}
                            error={lowBalanceEmailError}
                            helperText={
                                lowBalanceEmailError &&
                                isConnected &&
                                isAutoTopupEnabled &&
                                topUpAmount > 0
                                    ? t(
                                          'company.low_balance_field_not_empty_topup'
                                      )
                                    : ''
                            }
                        />
                        <SubTitle>
                            {t('company.separate_emails_comma')}
                        </SubTitle>
                    </GridContainer>

                    <GridContainer
                        // mt={3}
                        container
                        borderTop={'1px solid #D0D5DD'}
                    >
                        <AutoTopUpSettings
                            currentCompany={currentCompany}
                            isAutoTopupEnabled={isAutoTopupEnabled}
                            setIsAutoTopupEnabled={setIsAutoTopupEnabled}
                            topUpAmount={topUpAmount}
                            setTopUpAmount={setTopUpAmount}
                            onSaveAndConnect={handleSaveAndConnect}
                            onFormatError={setTopUpFormatError}
                        />
                    </GridContainer>

                    <FormActionButtons
                        show={isDirty}
                        onSave={handleSaveChanges}
                        onCancel={handleCancel}
                        isLoading={updateCompanyDetails.isPending}
                        disabled={hasFormatError}
                    />
                </Card>
            </Grid>
        </Grid>
    );
};
